import React, { useState, useEffect } from 'react';
// Imports Firebase Auth pour la sécurité du mot de passe
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth'; 
// Imports Firestore
import { doc, setDoc, getDoc, getDocs, collection } from 'firebase/firestore'; 
import { db } from '../firebase'; 
// Imports des icônes pour un meilleur design
import { User, Mail, School, Key, Lock, Loader2, Save, X, CheckCircle, Fingerprint } from 'lucide-react'; 

/**
 * Composant pour l'initialisation du profil et la gestion de la sécurité (mot de passe).
 * Affiche l'initialisation (Nom/École) si profileCompleted=false, sinon affiche
 * les infos du profil existant et le formulaire de changement de mot de passe.
 */
const UserProfileForm = ({ onProfileComplete, setError, setMessage }) => {
    const [uid, setUid] = useState(null);
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [schoolId, setSchoolId] = useState('');
    const [role, setRole] = useState('etudiant');
    const [loading, setLoading] = useState(true);
    const [schools, setSchools] = useState([]);
    const [profileCompleted, setProfileCompleted] = useState(false); 

    // --- États pour le Changement de Mot de Passe ---
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [isPasswordChanging, setIsPasswordChanging] = useState(false);
    // --------------------------------------------------------

    useEffect(() => {
        const auth = getAuth();
        const user = auth.currentUser;

        if (user) {
            setUid(user.uid);
            setEmail(user.email || '');
            setDisplayName(user.displayName || '');

            // Charger les écoles et vérifier le profil existant
            const fetchData = async () => {
                try {
                    // Charger les écoles
                    const schoolsSnapshot = await getDocs(collection(db, 'schools'));
                    setSchools(schoolsSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
                    
                    // Vérifier le profil
                    const userDocRef = doc(db, 'users', user.uid);
                    const userDocSnap = await getDoc(userDocRef);
                    if (userDocSnap.exists()) {
                        const data = userDocSnap.data();
                        setSchoolId(data.schoolId || '');
                        setDisplayName(data.displayName || user.displayName || ''); // Utiliser Firestore si disponible
                        setRole(data.role || 'etudiant');
                        setProfileCompleted(data.profileCompleted || false); 
                    }
                } catch (error) {
                    console.error("Erreur de chargement des données:", error);
                    setError("Impossible de charger les données du profil.");
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
        } else {
            setError("Aucun utilisateur connecté pour gérer un profil.");
            setLoading(false);
        }
    }, [setError]);

    // --- LOGIQUE D'INITIALISATION DU PROFIL (LORS DE LA PREMIÈRE CONNEXION) ---
    const handleInitializeProfile = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!uid || !schoolId || !displayName) {
            setError("Veuillez remplir votre nom complet et sélectionner une école.");
            return;
        }

        try {
            const userDocRef = doc(db, 'users', uid);
            await setDoc(userDocRef, {
                uid: uid,
                email: email,
                displayName: displayName,
                schoolId: schoolId,
                role: "etudiant", // Le rôle est forcé ici pour la première initialisation
                profileCompleted: true, 
                createdAt: new Date(),
            }, { merge: true });

            setMessage("Profil utilisateur initialisé avec succès !");
            setProfileCompleted(true); 
            onProfileComplete("etudiant", schoolId); 
        } catch (error) {
            console.error("Erreur lors de l'initialisation du profil:", error);
            setError(`Erreur lors de l'initialisation du profil: ${error.message}`);
        }
    };

    // --- LOGIQUE DE CHANGEMENT DE MOT DE PASSE (SÉCURITÉ) ---
    const handleChangePassword = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        const auth = getAuth();
        const user = auth.currentUser;

        if (!user || !user.email) {
            setError("Le changement de mot de passe n'est pas disponible pour les comptes sociaux.");
            return;
        }
        
        if (newPassword !== confirmNewPassword) { setError("Les nouveaux mots de passe ne correspondent pas."); return; }
        if (newPassword.length < 6) { setError("Le nouveau mot de passe doit contenir au moins 6 caractères."); return; }
        if (!currentPassword) { setError("Veuillez entrer votre mot de passe actuel."); return; }

        setIsPasswordChanging(true);

        try {
            // Re-authentification de l'utilisateur pour des raisons de sécurité
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);
            
            // Mise à jour du mot de passe
            await updatePassword(user, newPassword);

            setMessage("Mot de passe mis à jour avec succès ! Vous serez déconnecté.");
            await auth.signOut(); // Déconnexion forcée
            
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
        } catch (error) {
            let errorMessage = "Échec de la mise à jour du mot de passe.";
            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                errorMessage = "Mot de passe actuel incorrect. Veuillez réessayer.";
            } else {
                console.error("Erreur de changement de mot de passe:", error);
                errorMessage = `Erreur: ${error.message}`;
            }
            setError(errorMessage);
        } finally {
            setIsPasswordChanging(false);
        }
    };

    if (loading) return (
        <div className="p-8 text-center">
            <Loader2 className="animate-spin h-8 w-8 text-blue-500 inline-block mr-2" />
            <p className="text-gray-700">Chargement du profil...</p>
        </div>
    );

    // --- Rendu du composant ---
    return (
        <div className="max-w-xl mx-auto space-y-8 p-4">
            
            {/* 1. INITIALISATION DU PROFIL (Visible SEULEMENT si profileCompleted est FALSE) */}
            {!profileCompleted && (
                <form onSubmit={handleInitializeProfile} className="p-6 space-y-4 bg-yellow-50 border-2 border-yellow-300 shadow-lg rounded-xl">
                    <h2 className="text-2xl font-bold border-b pb-2 text-yellow-800 flex items-center">
                        <User size={24} className="mr-3" /> Compléter votre profil (Action requise)
                    </h2>
                    <p className="text-sm text-gray-600">Email: {email}</p>
                    
                    <div>
                        <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">Nom complet</label>
                        <input
                            type="text"
                            id="displayName"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3"
                            required
                        />
                    </div>
                    
                    <div>
                        <label htmlFor="schoolId" className="block text-sm font-medium text-gray-700">École</label>
                        <select
                            id="schoolId"
                            value={schoolId}
                            onChange={(e) => setSchoolId(e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3"
                            required
                        >
                            <option value="">Sélectionner une école</option>
                            {schools.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>

                    <button type="submit" className="px-4 py-3 bg-yellow-600 text-white rounded-lg font-semibold hover:bg-yellow-700 w-full transition duration-150">
                        Enregistrer et Accéder au Dashboard
                    </button>
                </form>
            )}

            {/* 2. AFFICHAGE DU PROFIL EXISTANT (Visible SEULEMENT si profileCompleted est TRUE) */}
            {profileCompleted && (
                <div className="p-6 bg-white shadow-lg rounded-xl border border-gray-200">
                    <h2 className="text-2xl font-bold mb-4 text-indigo-600 flex items-center">
                        <User size={24} className="mr-3" /> Informations du Profil
                    </h2>
                    
                    <div className="space-y-4">
                        {/* Ligne Nom */}
                        <div className="flex items-center text-gray-700 p-2 bg-gray-50 rounded-md border border-gray-100">
                            <User size={18} className="mr-3 text-blue-500" />
                            <span className="font-semibold w-24">Nom :</span>
                            <span className="truncate">{displayName}</span>
                        </div>

                        {/* Ligne Email */}
                        <div className="flex items-center text-gray-700 p-2 bg-gray-50 rounded-md border border-gray-100">
                            <Mail size={18} className="mr-3 text-blue-500" />
                            <span className="font-semibold w-24">Email :</span>
                            <span className="truncate">{email}</span>
                        </div>

                        {/* Ligne UID (NOUVEAU) */}
                        <div className="flex items-center text-gray-700 p-2 bg-gray-50 rounded-md border border-gray-100">
                            <Fingerprint size={18} className="mr-3 text-red-500" /> 
                            <span className="font-semibold w-24">UID Auth :</span>
                            <span className="truncate font-mono text-xs select-all text-red-700">{uid}</span> 
                        </div>

                        {/* Ligne Rôle */}
                        <div className="flex items-center text-gray-700 p-2 bg-gray-50 rounded-md border border-gray-100">
                            <Key size={18} className="mr-3 text-blue-500" />
                            <span className="font-semibold w-24">Rôle :</span>
                            <span className="capitalize">{role}</span>
                        </div>
                        
                        {/* Ligne École */}
                        <div className="flex items-center text-gray-700 p-2 bg-gray-50 rounded-md border border-gray-100">
                            <School size={18} className="mr-3 text-blue-500" />
                            <span className="font-semibold w-24">École :</span>
                            <span className="truncate">
                                {schools.find(s => s.id === schoolId)?.name || 'N/A'}
                            </span>
                        </div>
                    </div>
                </div>
            )}
            
            {/* 3. FORMULAIRE DE CHANGEMENT DE MOT DE PASSE (Toujours affiché si l'utilisateur est connecté par email/mot de passe) */}
            <div className="p-6 bg-white shadow-lg rounded-xl border border-gray-200">
                <h2 className="text-2xl font-bold mb-4 text-red-600 flex items-center">
                    <Lock size={24} className="mr-3" /> Changer le Mot de Passe
                </h2>
                
                {email ? (
                    <form onSubmit={handleChangePassword} className="space-y-4 pt-2">
                        {/* Champ Mot de Passe Actuel */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 flex items-center">
                                <Key size={16} className="mr-2" /> Mot de Passe Actuel
                            </label>
                            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} 
                                className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:border-red-500 focus:ring-red-500" required />
                        </div>
                        
                        {/* Champ Nouveau Mot de Passe */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 flex items-center">
                                <Lock size={16} className="mr-2" /> Nouveau Mot de Passe
                            </label>
                            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} 
                                className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:border-red-500 focus:ring-red-500" required />
                        </div>
                        
                        {/* Champ Confirmation */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 flex items-center">
                                <CheckCircle size={16} className="mr-2" /> Confirmer
                            </label>
                            <input type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} 
                                className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:border-red-500 focus:ring-red-500" required />
                        </div>

                        <button
                            type="submit"
                            disabled={isPasswordChanging}
                            className={`w-full py-3 px-4 border border-transparent rounded-lg shadow-lg text-sm font-medium text-white flex items-center justify-center transition duration-200 ${
                                isPasswordChanging ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                            }`}
                        >
                            {isPasswordChanging ? (
                                <Loader2 size={18} className="animate-spin mr-2" />
                            ) : (
                                <Save size={18} className="mr-2" />
                            )}
                            {isPasswordChanging ? 'Mise à jour en cours...' : 'Changer le Mot de Passe'}
                        </button>
                    </form>
                ) : (
                    <p className="text-sm text-yellow-700 bg-yellow-100 p-3 rounded-md flex items-center">
                        <X size={16} className="mr-2" /> Le changement de mot de passe n'est pas disponible pour les comptes connectés via un fournisseur social.
                    </p>
                )}
            </div>
        </div>
    );
};

export default UserProfileForm;